import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { Card, ErrorBanner, RatingStars } from './shared';
import { Stats, getStats } from './api';

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${SPACERS.small};
  margin-top: ${SPACERS.small};
`;

const StatBox = styled.div`
  border: 1px solid ${COLORS.greyLight};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
`;

const StatBoxValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${COLORS.ctaPrimary};
`;

const StatBoxLabel = styled.div`
  font-size: 13px;
  color: ${COLORS.grayDark};
`;

const HistogramRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  padding: 3px 0;
`;

const HistogramTrack = styled.div`
  flex: 1;
  height: 10px;
  background: ${COLORS.greyLight};
  border-radius: 999px;
  overflow: hidden;
`;

const HistogramFill = styled.div`
  height: 100%;
  background: ${COLORS.ctaPrimary};
  border-radius: 999px;
`;

const HistogramCount = styled.span`
  width: 40px;
  text-align: right;
  font-size: 13px;
  color: ${COLORS.grayDark};
`;

const TopTemplateRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${SPACERS.small};
  padding: 4px 0;
  border-bottom: 1px solid ${COLORS.greyLight};
  &:last-child {
    border-bottom: none;
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACERS.medium};
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StatsTab: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await getStats());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats.');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading && !stats) {
    return (
      <Card>
        <TrueImpactLoading />
      </Card>
    );
  }

  const maxCount = stats
    ? Math.max(1, ...Object.values(stats.rating_counts || {}))
    : 1;

  return (
    <>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {stats && (
        <>
          <Card>
            <TrueImpactText variant="heading-subheading">
              Overview
            </TrueImpactText>
            <StatGrid>
              <StatBox>
                <StatBoxValue>{stats.templates}</StatBoxValue>
                <StatBoxLabel>Templates</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>{stats.match_queries}</StatBoxValue>
                <StatBoxLabel>Match Queries</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>{stats.feedback_count}</StatBoxValue>
                <StatBoxLabel>Feedback Submitted</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>
                  {stats.average_rating != null
                    ? stats.average_rating.toFixed(2)
                    : '—'}
                </StatBoxValue>
                <StatBoxLabel>Average Rating</StatBoxLabel>
              </StatBox>
              <StatBox>
                <StatBoxValue>{stats.unused_templates}</StatBoxValue>
                <StatBoxLabel>Unused Templates</StatBoxLabel>
              </StatBox>
            </StatGrid>
          </Card>

          <TwoColumn>
            <Card style={{ marginBottom: 0 }}>
              <TrueImpactText variant="heading-subheading">
                Rating Distribution
              </TrueImpactText>
              <div style={{ marginTop: SPACERS.small }}>
                {[5, 4, 3, 2, 1].map(r => {
                  const count = stats.rating_counts?.[String(r)] ?? 0;
                  return (
                    <HistogramRow key={r}>
                      <RatingStars value={r} size={12} />
                      <HistogramTrack>
                        <HistogramFill
                          style={{ width: `${(count / maxCount) * 100}%` }}
                        />
                      </HistogramTrack>
                      <HistogramCount>{count}</HistogramCount>
                    </HistogramRow>
                  );
                })}
              </div>
            </Card>

            <Card style={{ marginBottom: 0 }}>
              <TrueImpactText variant="heading-subheading">
                Top Templates
              </TrueImpactText>
              <TrueImpactText
                variant="body-small"
                color="grayDark"
                style={{ display: 'block', marginTop: 4 }}
              >
                Most frequent appearances in match results.
              </TrueImpactText>
              <div style={{ marginTop: SPACERS.small }}>
                {stats.top_templates.length === 0 ? (
                  <TrueImpactText variant="body-small" color="grayDark">
                    No match data yet.
                  </TrueImpactText>
                ) : (
                  stats.top_templates.map(t => (
                    <TopTemplateRow key={t.template_id}>
                      <TrueImpactText variant="body-small">
                        {t.name ?? (
                          <em style={{ color: COLORS.grayDark }}>
                            deleted template
                          </em>
                        )}
                      </TrueImpactText>
                      <TrueImpactText variant="body-small-bold">
                        {t.appearances}
                      </TrueImpactText>
                    </TopTemplateRow>
                  ))
                )}
              </div>
            </Card>
          </TwoColumn>
        </>
      )}
    </>
  );
};

export default StatsTab;
